import { useQuery } from '@tanstack/react-query';

import { flattenMetadataForRequest } from '@/components/metadata-filters/urlParams';
import type { SupportedTimeZone } from '@/constants/date';
import type { OrgAnalyticsRequest, OrgAnalyticsResponse } from '@/pages/analytics/types';
import { applyTrendBucketLabels, buildTrendBucketPlan } from '@/pages/analytics/utils/dateWindow';
import { analyticsService } from '@/services/api';
import { naiveDateToUtc } from '@/utils/date';
import { serializeMultiValueParam } from '@/utils/urlParams';

/** Every trend list in the QC analytics response; the backend echoes each bucket's UTC start. */
export const QC_TREND_KEYS = [
  'score_trend',
  'compliance_trend',
  'escalation_trend',
  'call_volume_trend',
] as const;

export const analyticsQueryKeys = {
  all: ['compass', 'analytics'] as const,
  org: (params: OrgAnalyticsRequest) => [...analyticsQueryKeys.all, 'org', params] as const,
};

interface UseOrgAnalyticsArgs {
  organizationId: string | null;
  programIds?: string[];
  callDirections?: string[];
  start?: string;
  end?: string;
  timeZone: SupportedTimeZone;
  metadata?: Record<string, string[]>;
}

export const useOrgAnalytics = ({
  organizationId,
  programIds,
  callDirections,
  start,
  end,
  timeZone,
  metadata,
}: UseOrgAnalyticsArgs) => {
  const program_ids = programIds && programIds.length ? programIds.join(',') : undefined;
  const trendPlan = buildTrendBucketPlan(start, end, timeZone);
  const params: OrgAnalyticsRequest = {
    organization_id: organizationId ?? '',
    program_ids,
    call_directions: serializeMultiValueParam(callDirections),
    start: start ? naiveDateToUtc(start, timeZone, 'start') : undefined,
    end: end ? naiveDateToUtc(end, timeZone, 'end') : undefined,
    // Structured metadata drives the cache key; it is flattened for the wire below.
    metadata,
    ...trendPlan?.params,
  };
  const { organization_id, metadata: metadataFilters, ...rest } = params;
  // Re-emit metadata as flat `metadata.<key>=v1,v2` dot-notation query params the
  // backend reads (the structured field never goes on the wire).
  const queryParams = { ...rest, ...flattenMetadataForRequest(metadataFilters) };

  return useQuery({
    queryKey: analyticsQueryKeys.org(params),
    queryFn: async () => {
      const resp = (await analyticsService.getOrgAnalytics(
        { organization_id },
        { params: queryParams },
        // The generated proto type has by_metadata as {entries: T[]} wrappers, but the
        // HTTP handler flattens it to bare arrays, matching OrgAnalyticsResponse at runtime.
      )) as unknown as OrgAnalyticsResponse;
      // The backend echoes each bucket's UTC start as the trend date; relabel it
      // to the local YYYY-MM-DD the charts expect.
      return trendPlan ? applyTrendBucketLabels(resp, QC_TREND_KEYS, trendPlan.labelByStart) : resp;
    },
    // Gate on start/end too: the date range is applied via a post-render effect
    // (useAnalyticsFiltersState), so on mount they are briefly empty. Firing then
    // issues a spurious param-less request that the backend answers with its own
    // (wide, all-time) default range — a different dataset from the date-filtered
    // one. That second cache entry is what surfaced as duplicate/mismatched charts
    // on navigation back to analytics. See AQ-476.
    enabled: !!organizationId && !!start && !!end,
    staleTime: 60_000,
    gcTime: 5 * 60_000,
  });
};
